import {
    AlertCircle,
    CheckCircle2,
    Info,
    X,
    type LucideIcon,
} from 'lucide-react'
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { cn } from '~/lib/utils'

// 통합 토스트 — 화면 우하단 스택. ToastProvider(Context + 포털) + useToast() 훅.
// toast.success/error/info(message, opts?) → 자동 소멸(~3.5s, 호버 시 일시정지),
// 수동 닫기(X), 선택적 액션 버튼(opts.action={label,onClick}). 미니멀 라이트, 다크 자동 적응.

type ToastVariant = 'success' | 'error' | 'info'

export interface ToastAction {
    label: string
    onClick: () => void
}

export interface ToastOptions {
    /** 자동 소멸까지(ms). 0이면 자동 소멸 안 함. 기본 3500. */
    duration?: number
    /** 선택 액션 버튼(예: "되돌리기"). */
    action?: ToastAction
}

interface ToastItem extends ToastOptions {
    id: number
    message: string
    variant: ToastVariant
}

interface ToastApi {
    success: (message: string, opts?: ToastOptions) => number
    error: (message: string, opts?: ToastOptions) => number
    info: (message: string, opts?: ToastOptions) => number
    dismiss: (id: number) => void
}

const ToastContext = createContext<ToastApi | null>(null)

const DEFAULT_DURATION = 3500

// 변형별 좌측 아이콘 + 색(시맨틱 토큰만). 카드 자체는 popover 표면으로 절제.
const VARIANT: Record<ToastVariant, { icon: LucideIcon; color: string }> = {
    success: { icon: CheckCircle2, color: 'text-success' },
    error: { icon: AlertCircle, color: 'text-destructive' },
    info: { icon: Info, color: 'text-info' },
}

/** 토스트 Context를 제공하고 포털 스택을 렌더한다. 앱 루트를 감싼다. */
export function ToastProvider({ children }: { children: ReactNode }) {
    const [toasts, setToasts] = useState<ToastItem[]>([])
    const idRef = useRef(0)

    const dismiss = useCallback((id: number) => {
        setToasts((list) => list.filter((t) => t.id !== id))
    }, [])

    const push = useCallback(
        (variant: ToastVariant, message: string, opts?: ToastOptions) => {
            const id = ++idRef.current
            setToasts((list) => [...list, { id, message, variant, ...opts }])
            return id
        },
        []
    )

    const api = useMemo<ToastApi>(
        () => ({
            success: (m, o) => push('success', m, o),
            error: (m, o) => push('error', m, o),
            info: (m, o) => push('info', m, o),
            dismiss,
        }),
        [push, dismiss]
    )

    return (
        <ToastContext.Provider value={api}>
            {children}
            {typeof document !== 'undefined'
                ? createPortal(
                      <div
                          aria-live='polite'
                          className='pointer-events-none fixed right-4 bottom-4 z-[100] flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2'>
                          {toasts.map((t) => (
                              <ToastCard
                                  key={t.id}
                                  toast={t}
                                  onDismiss={() => dismiss(t.id)}
                              />
                          ))}
                      </div>,
                      document.body
                  )
                : null}
        </ToastContext.Provider>
    )
}

// 개별 토스트 카드. 자동 소멸 타이머를 들고 있고, 호버 시 일시정지한다.
function ToastCard({
    toast,
    onDismiss,
}: {
    toast: ToastItem
    onDismiss: () => void
}) {
    const { icon: Icon, color } = VARIANT[toast.variant]
    const duration = toast.duration ?? DEFAULT_DURATION
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const clear = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current)
            timerRef.current = null
        }
    }, [])

    const arm = useCallback(() => {
        if (duration <= 0) return
        clear()
        timerRef.current = setTimeout(onDismiss, duration)
    }, [duration, clear, onDismiss])

    useEffect(() => {
        arm()
        return clear
    }, [arm, clear])

    return (
        <div
            role='status'
            onMouseEnter={clear}
            onMouseLeave={arm}
            className='border-border bg-popover text-popover-foreground pointer-events-auto flex items-start gap-2.5 rounded-[var(--radius)] border p-3 text-[13px] shadow-sm'>
            <Icon className={cn('mt-px size-4 shrink-0', color)} />
            <div className='min-w-0 flex-1'>
                <p className='break-words'>{toast.message}</p>
                {toast.action ? (
                    <button
                        type='button'
                        onClick={() => {
                            toast.action?.onClick()
                            onDismiss()
                        }}
                        className='text-primary focus-visible:ring-ring/40 mt-1.5 rounded-[var(--radius-sm)] text-[12px] font-medium underline-offset-2 outline-none hover:underline focus-visible:ring-2'>
                        {toast.action.label}
                    </button>
                ) : null}
            </div>
            <button
                type='button'
                aria-label='닫기'
                onClick={onDismiss}
                className='text-muted-foreground hover:text-foreground focus-visible:ring-ring/40 -m-1 shrink-0 rounded-[var(--radius-sm)] p-1 outline-none focus-visible:ring-2'>
                <X className='size-3.5' />
            </button>
        </div>
    )
}

/** 토스트 API 훅. ToastProvider 내부에서만 호출한다. */
export function useToast(): ToastApi {
    const ctx = useContext(ToastContext)
    if (!ctx) {
        throw new Error('useToast는 ToastProvider 안에서만 사용할 수 있어요.')
    }
    return ctx
}
